import React from "react";
import Navbar from "../../components/Navbar";
import { Check, X, ArrowRight, Zap, DollarSign, Gauge, RefreshCw } from "lucide-react";

const comparisonData = {
  pinecone: {
    name: "Pinecone",
    tagline: "Fully managed vector database",
    pros: [
      "Zero infrastructure management required",
      "Excellent documentation and developer experience",
      "Built-in hybrid search capabilities",
      "Strong enterprise support and SLAs",
      "Auto-scaling with serverless options"
    ],
    cons: [
      "Higher pricing at scale",
      "Limited customization options",
      "Vendor lock-in concerns",
      "Less control over indexing algorithms",
      "Region availability constraints"
    ],
    pricing: {
      tier: "Premium",
      description: "Starts at $70/month for 100K vectors. Scales based on pod size and replicas.",
      note: "Best for: Teams prioritising ease of use over cost optimisation"
    },
    performance: {
      latency: "Sub-50ms p99 latency",
      throughput: "High QPS with auto-scaling"
    }
  },
  qdrant: {
    name: "Qdrant",
    tagline: "Open-source vector search engine",
    pros: [
      "Open-source with self-hosting option",
      "Advanced filtering and payload support",
      "Cost-effective for large datasets",
      "Flexible deployment (Docker, Kubernetes, Cloud)",
      "Active community and rapid development"
    ],
    cons: [
      "Requires more DevOps expertise",
      "Smaller ecosystem compared to competitors",
      "Self-hosting means infrastructure responsibility",
      "Documentation gaps for advanced features",
      "Cloud offering still maturing"
    ],
    pricing: {
      tier: "Flexible",
      description: "Free self-hosted. Cloud starts at $25/month. Pay for compute resources only.",
      note: "Best for: Cost-conscious teams with DevOps capabilities"
    },
    performance: {
      latency: "Sub-100ms for most queries",
      throughput: "Optimised for filtered searches"
    }
  }
};

const migrationDetails = {
  difficulty: "Medium",
  timeEstimate: "2-5 hours for 1M vectors",
  considerations: [
    "Index configuration mapping required",
    "Metadata schema transformation needed",
    "Query syntax differences to address",
    "Testing recommended for filtered queries"
  ],
  howWeHelp: [
    "Automated index configuration translation",
    "Zero-downtime migration with dual-write strategy",
    "Pre-migration compatibility analysis",
    "Post-migration query performance validation",
    "Rollback capability if issues detected"
  ]
};

const ComparisonSection = ({ title, icon: Icon, children }) => (
  <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-8">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
        <Icon className="w-5 h-5 text-black" />
      </div>
      <h2 className="text-2xl font-bold text-white">{title}</h2>
    </div>
    {children}
  </div>
);

const ProConList = ({ items, isPositive }) => (
  <ul className="space-y-3">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-3">
        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isPositive ? 'bg-white/20' : 'bg-gray-700'
        }`}>
          {isPositive ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <X className="w-3 h-3 text-red-500" />
          )}
        </div>
        <span className="text-gray-300 text-sm">{item}</span>
      </li>
    ))}
  </ul>
);

const DatabaseCard = ({ data }) => (
  <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
    <h3 className="text-2xl font-bold text-white mb-2">{data.name}</h3>
    <p className="text-gray-400 text-sm mb-6">{data.tagline}</p>
    
    <div className="space-y-6">
      <div>
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Check className="text-green-500 w-4 h-4" />
          Advantages
        </h4>
        <ProConList items={data.pros} isPositive={true} />
      </div>
      
      <div>
        <h4 className="text-gray-400 font-semibold mb-3 flex items-center gap-2">
          <X className="text-red-500 w-4 h-4" />
          Limitations
        </h4>
        <ProConList items={data.cons} isPositive={false} />
      </div>
    </div>
  </div>
);

const PricingCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-white">{name}</h3>
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-800 text-gray-300 border border-gray-700">
        {data.tier}
      </span>
    </div>
    <p className="text-gray-300 text-sm mb-3">{data.description}</p>
    <p className="text-gray-500 text-xs italic">{data.note}</p>
  </div>
);

const PerformanceCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-white">{name}</h3>
    </div>
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Query Latency:</span>
        <span className="text-gray-300">{data.latency}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Throughput:</span>
        <span className="text-gray-300">{data.throughput}</span>
      </div>
    </div>
  </div>
);

export default function PineconeVsQdrant() {
  return (
    <div className="bg-black min-h-screen px-6">
      <Navbar />
      <div className="max-w-6xl py-28 mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">
            Pinecone vs Qdrant: Comparison 2026
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto">
            Detailed comparison of features, pricing, performance, and migration complexity 
            to help you choose the right vector database for your AI applications
          </p>
        </div>

        {/* Side-by-Side Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <DatabaseCard data={comparisonData.pinecone} />
          <DatabaseCard data={comparisonData.qdrant} />
        </div>

        {/* Pricing Comparison */}
        <ComparisonSection title="Pricing Comparison" icon={DollarSign}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PricingCard data={comparisonData.pinecone.pricing} name="Pinecone" />
            <PricingCard data={comparisonData.qdrant.pricing} name="Qdrant" />
          </div>
          <div className="mt-8 p-6 bg-gray-800 border border-gray-700 rounded-lg">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Cost Analysis:</strong> For 10M vectors, 
              Qdrant self-hosted can save 60-70% compared to Pinecone. However, factor in 
              DevOps time and infrastructure management costs when calculating true TCO.
            </p>
          </div>
        </ComparisonSection>

        {/* Performance Comparison */}
        <ComparisonSection title="Performance Metrics" icon={Gauge}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PerformanceCard data={comparisonData.pinecone.performance} name="Pinecone" />
            <PerformanceCard data={comparisonData.qdrant.performance} name="Qdrant" />
          </div>
          <div className="mt-8 p-6 bg-gray-800 border border-gray-700 rounded-lg">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Performance Considerations:</strong> Both offer 
              excellent performance. Pinecone edges ahead for pure vector similarity, while 
              Qdrant excels in complex filtered queries and payload searches.
            </p>
          </div>
        </ComparisonSection>

        {/* Migration Difficulty */}
        <ComparisonSection title="Migration from Pinecone to Qdrant" icon={RefreshCw}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Migration Complexity</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Difficulty Level:</span>
                  <span className="text-gray-300 font-semibold">{migrationDetails.difficulty}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Estimated Time:</span>
                  <span className="text-gray-300 text-sm">{migrationDetails.timeEstimate}</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Key Considerations</h3>
              <ul className="space-y-2">
                {migrationDetails.considerations.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ComparisonSection>

        {/* How Vector Migration Helps */}
        <div className="mt-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-12 text-center border border-gray-700">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
              <Zap className="w-8 h-8 text-black" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">
              How Vector Migration Simplifies Your Switch
            </h2>
            <p className="text-gray-400 mb-8">
              We handle all the complexity of migrating from Pinecone to Qdrant, 
              so you can focus on building great AI applications.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 text-left">
              {migrationDetails.howWeHelp.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-black/40 rounded-lg p-4 border border-gray-800">
                  <Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
            
            {/* <button className="bg-white text-black px-8 py-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors inline-flex items-center gap-2">
              Start Your Migration Today
              <ArrowRight className="w-5 h-5" />
            </button> */}
          </div>
        </div>

        {/* Final Recommendation */}
        <div className="mt-16 bg-gray-900 border border-gray-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Which Should You Choose?</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              <strong className="text-white">Choose Pinecone if:</strong> You want a fully managed 
              solution with minimal DevOps overhead, need enterprise-grade support, and prefer paying 
              premium pricing for simplicity and reliability.
            </p>
            <p>
              <strong className="text-white">Choose Qdrant if:</strong> You have DevOps resources, 
              want more control over infrastructure, need cost optimisation at scale, or require 
              advanced filtering capabilities for complex search scenarios.
            </p>
            <p className="text-sm text-gray-500 italic">
              Both are excellent choices. Your decision should align with your team's technical 
              capabilities, budget constraints, and long-term scalability requirements.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}